import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { AsyncState, Newsletter, NewsletterVersion } from '@/types';
import * as versionService from '@/services/versionService';
import { publishNewsletterThunk } from '@/store/slices/newslettersSlice';

interface VersionsState extends AsyncState {
  items: NewsletterVersion[];
}

const initialState: VersionsState = {
  items: [],
  status: 'idle',
  error: null,
};

export const fetchVersionsForNewsletter = createAsyncThunk(
  'versions/fetchForNewsletter',
  async (newsletterId: string) => {
    return versionService.listVersions(newsletterId);
  },
);

export const snapshotVersionThunk = createAsyncThunk(
  'versions/snapshot',
  async ({
    newsletter,
    actionItemIds,
    note,
  }: {
    newsletter: Newsletter;
    actionItemIds: string[];
    note?: string;
  }) => {
    return versionService.snapshotNewsletter(newsletter, actionItemIds, note);
  },
);

const versionsSlice = createSlice({
  name: 'versions',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchVersionsForNewsletter.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchVersionsForNewsletter.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload;
      })
      .addCase(fetchVersionsForNewsletter.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? 'Failed to load version history';
      })
      .addCase(snapshotVersionThunk.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
      })
      .addCase(publishNewsletterThunk.fulfilled, (state, action) => {
        state.items.unshift(action.payload.version);
      });
  },
});

export default versionsSlice.reducer;
